<template>
    <div>
        <div v-if="shift">
            <div class="flex justify-between mb-4">
                <div class="border-b border-grayxlight text-[13px]">Основные данные</div>
                <button-icon v-if="store.currentShift?.id" class="text-darkred opacity-40 hover:opacity-100" @click="onDelete">
                    <IconTrash />
                </button-icon>
            </div>

            <div class="flex mb-4">
                <div class="mr-5 flex-1">
                    <div class="text-xs text-gray pb-1">Тип дня</div>
                    <dropdown
                        v-if="!isDayStateDocumentShift"
                        v-model="dayType"
                        :options="dayTypes"
                        @click="shiftFieldClick"
                        :disabled="!canEditDayType || editShiftDisable"
                    >
                        {{ store.cutStr(dayType.name, 23) }}
                    </dropdown>
                    <div v-else class="text-darkgray text-sm pt-2">
                        {{ store.cutStr(currentDayStateDocumentTypeName, 23) }}
                    </div>
                </div>
                <div class="mr-5 flex-1">
                    <div v-if="shouldShowFullEditor">
                        <div class="text-xs text-gray pb-1">Должность</div>
                        <dropdown v-model="position" class="" :options="store.positions" @click="shiftFieldClick"
                            :disabled="editShiftDisable">
                            {{ store.cutStr(position.name, 23) }}
                        </dropdown>
                    </div>
                </div>
            </div>

            <div v-if="shouldShowDurationEditor" class="flex mb-4">
                <div class="mr-5 flex-1">
                    <div class="text-xs text-gray pb-1">Продолжительность</div>
                    <time-picker
                        v-model="durationDisplay"
                        @click="shiftFieldClick"
                        :disabled="!canEditDayStateDuration"
                    />
                </div>
                <div class="mr-5 flex-1"></div>
            </div>

            <div v-if="shouldShowFullEditor">
                <checkbox class="text-sm" v-model="isOvertime" :disabled="disableOvertime || editShiftDisable || isOvertimeLockedByDayStateDocument"
                    @click="shiftFieldClick">Дополнительный табель</checkbox>

                <div class="mb-4">
                    <div class="flex justify-between mb-3">
                        <div class="border-b border-grayxlight text-[13px]">Плановая смена</div>
                    </div>

                    <div class="flex">
                        <div class="mr-5 flex-1">
                            <div class="text-xs text-gray pb-1">Время начала</div>
                            <time-picker v-model="start" @click="shiftFieldClick" :disabled="editShiftDisable"
                                v-model:is-valid="planStartTimeValid"
                                required
                                :bgclr="planTimeError && !start ? '#ffd5c3' : undefined" />
                        </div>
                        <div class="mr-5 flex-1">
                            <div class="text-xs text-gray pb-1">Время окончания</div>
                            <time-picker v-model="end" @click="shiftFieldClick" :disabled="editShiftDisable"
                                v-model:is-valid="planEndTimeValid"
                                required
                                :bgclr="planTimeError && !end ? '#ffd5c3' : undefined" />
                        </div>
                    </div>
                    <div v-if="planTimeError" class="text-xs text-warning mt-1">
                        Заполните плановое время начала и окончания смены
                    </div>
                </div>

                <div class="mb-2">
                    <div class="flex justify-between mb-3">
                        <div class="border-b border-grayxlight text-[13px]">Фактическая смена</div>
                    </div>

                    <div class="flex">
                        <div class="mr-5 flex-1">
                            <div class="text-xs text-gray pb-1">Время начала</div>
                            <time-picker :bgclr="bgrnd(shift.violation?.start_status)" v-model="startFact"
                                v-model:is-valid="factStartTimeValid"
                                @click="shiftFieldClick"
                                :disabled="!canEditFact || isFutureOrCurrentShift || isOwnShift || editShiftDisable" />
                            <div v-if="overtimeFactStartError" class="text-xs text-warning mt-1">
                                {{ overtimeFactStartErrorMessage }}
                            </div>
                            <div v-else class="text-xs" :class="`text-${shift.violation?.start_status}`">{{
                                shift.violation?.start_msg }}</div>
                        </div>
                        <div class="mr-5 flex-1">
                            <div class="text-xs text-gray pb-1">Время окончания</div>
                            <time-picker :bgclr="bgrnd(shift.violation?.end_status)" v-model="endFact"
                                v-model:is-valid="factEndTimeValid"
                                @click="shiftFieldClick"
                                :disabled="!canEditFact || isFutureOrCurrentShift || isOwnShift || editShiftDisable" />
                            <div class="text-xs" :class="`text-${shift.violation?.end_status}`">{{
                                shift.violation?.end_msg
                                }}</div>
                        </div>
                    </div>
                </div>

                <div class="ml-1 mt-5 flex items-center">
                    <icon width="16" height="13" class="mr-1"
                        :class="{ 'text-darkyellow': shift.violation?.general_status === 'moderate', 'text-red': shift.violation?.general_status === 'warning' }"
                        v-if="shift.violation?.general_status === 'moderate' || shift.violation?.general_status === 'warning'">
                        <IconWarning />
                    </icon>
                    <icon class="mr-1 text-green" v-if="shift.violation?.general_status === 'ok'">
                        <IconApprove />
                    </icon>
                    <span class="text-sm ">{{ message }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { DayType, Schedule, Shift } from '@/types'
import IconTrash from '../../ui/icons/IconTrash.vue'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import TimePicker from '@/components/TimePicker.vue'
import { Option } from '@/types'
import IconWarning from '@/ui/icons/IconWarning.vue'
import IconApprove from '@/ui/icons/IconApprove.vue'
import { decimalHoursToPickerDuration, pickerDurationToDecimalHours } from '@/utils/duration'
import { useToast } from 'vue-toast-notification'

dayjs.extend(utc)

import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'

const store = useScheduleStore()
const authStore = useAuthStore()
const toast = useToast({ position: 'top' })

interface Props {
    shift: Shift | null,
    dayTypes: DayType[],
    schedule: Schedule
}

const props = defineProps<Props>()
const shift = ref<any>(store.currentShift || props.shift || null)
const emit = defineEmits(['delete'])
const isOvertime = ref(!!shift.value?.is_overtime)
const start = ref<string | null>(shift.value?.plan_work_start_at ? dayjs.utc(shift.value.plan_work_start_at).format('HH:mm') : shift.value?.plan_work_start_at ?? null)
const end = ref<string | null>(shift.value?.plan_work_end_at ? dayjs.utc(shift.value.plan_work_end_at).format('HH:mm') : shift.value?.plan_work_end_at ?? null)
const startFact = ref<string | null>(null)
const endFact = ref<string | null>(null)
const planStartTimeValid = ref(true)
const planEndTimeValid = ref(true)
const factStartTimeValid = ref(true)
const factEndTimeValid = ref(true)
const disableOvertime = ref(false)
const planTimeError = ref(false)
const isFutureOrCurrentShift = computed(() => dayjs().isBefore(dayjs(shift.value?.plan_work_end_at)))
const isPastShift = computed(() => dayjs(shift.value?.date).isBefore(dayjs(), 'day'))
const isOwnShift = computed(() => props.schedule.user.id === authStore.userInfo.id)
const canEditDayType = computed(() => authStore.permissions?.can_edit_day_type)
const canEditFact = computed(() => (
    authStore.permissions?.can_edit_fact_time ||
    (!props.schedule.user.smart_checkin_possible && isPastShift.value)
))
const canEditDayStateDuration = computed(() => authStore.permissions?.can_edit_day_state_duration)
const editShiftDisable = computed(() => !authStore.permissions?.can_edit_shift_schedule && shift.value?.id)
const bgrnd = (status: string) => {
    if (status === 'moderate') return '#fff5de'
    if (status === 'warning') return '#ffd5c3'
}

const durationDisplay = ref<string | null>(decimalHoursToPickerDuration(props.shift?.duration) || null)

const status = [
    { id: 'ok', text: 'Смена без нарушений' },
    { id: 'moderate', text: 'Смена с нарушениями' },
    { id: 'warning', text: 'Серьезное нарушение, внесите изменения' }
]

const dayType = ref<Option>({
    id: shift.value?.day_type || '',
    name: props.dayTypes.find(item => item.id === shift.value?.day_type)?.name || ''
})
const position = ref<Option>({
    id: shift.value?.position || '',
    name: store.positions.find(item => item.id === shift.value?.position)?.name || ''
})

const message = computed(() => {
    return status.find(item => item.id === shift.value.violation?.general_status)?.text
})

const shiftDayType = computed(() => store.dayTypes.find((daytype) => daytype.id === shift.value?.day_type))
const currentDayStateDocumentTypeName = computed(() => {
    return shiftDayType.value?.name || 'Кадровое отклонение'
})

const isWorkday = computed(() => {
    return !shiftDayType.value?.is_dayoff
})
const isDayStateDocumentShift = computed(() => Boolean(shift.value?.day_state_document))
const hasDayStateDocumentInCurrentDay = computed(() => store.currentShifts.some((item) => Boolean(item?.day_state_document)))
const isRegularShiftNextToDayStateDocument = computed(() => !isDayStateDocumentShift.value && hasDayStateDocumentInCurrentDay.value)
const isOvertimeLockedByDayStateDocument = computed(() => isRegularShiftNextToDayStateDocument.value)
const shouldShowDurationEditor = computed(() => isDayStateDocumentShift.value)
const shouldShowFullEditor = computed(() => isWorkday.value && !isDayStateDocumentShift.value)
const requiresPlanTime = computed(() => shouldShowFullEditor.value)
const overtimeFactStartErrorMessage = 'Фактическое время начала дополнительной смены не может быть раньше её планового времени начала'
const factStartDate = computed(() => {
    const date: string = shift.value.date
    // У ночной дополнительной смены утренняя часть плана относится к следующим суткам.
    if (isOvertime.value && start.value && end.value && startFact.value &&
        end.value < start.value && startFact.value <= end.value) {
        return dayjs.utc(date).add(1, 'day').format('YYYY-MM-DD')
    }

    return date
})
const overtimeFactStartError = computed(() => {
    if (!isOvertime.value || !start.value || !startFact.value) return false

    return dayjs.utc(`${factStartDate.value} ${startFact.value}`)
        .isBefore(dayjs.utc(`${shift.value.date} ${start.value}`))
})
const areTimePickersValid = computed(() => (
    planStartTimeValid.value &&
    planEndTimeValid.value &&
    factStartTimeValid.value &&
    factEndTimeValid.value
))

startFact.value = (shift.value?.fact_work_start_at) ? dayjs.utc(shift.value.fact_work_start_at).format('HH:mm') : null
endFact.value = (shift.value?.fact_work_end_at) ? dayjs.utc(shift.value.fact_work_end_at).format('HH:mm') : null

const checkValidPosition = () => {
    if (position.value.id !== props.schedule.position.id) {
        isOvertime.value = true
        disableOvertime.value = true
    } else {
        disableOvertime.value = false
    }
}

const isTimeChanged = (oldTime: any, newTime: any, fieldName: string) => {
    return dayjs.utc(oldTime[fieldName]).format('HH:mm') !== dayjs.utc(newTime[fieldName]).format('HH:mm')
}

const isShiftChanged = computed(() => {
    // get original shift for tracking changes 
    const originalShift = store.originalShifts.find(shift => shift.id === store.currentShift?.id)

    if (originalShift) {
        // check plan and fact time for changes
        if (isTimeChanged(originalShift, store.currentShift, 'plan_work_start_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'plan_work_end_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'fact_work_start_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'fact_work_end_at')) return true
        // check position
        else if (originalShift.position !== store.currentShift?.position) return true
        // check day type
        else if (originalShift.day_type !== store.currentShift.day_type) return true
        // check overtime
        else if (originalShift.is_overtime !== store.currentShift.is_overtime) return true
        // check duration for day state shifts
        else if (originalShift.duration !== store.currentShift?.duration) return true
    }
    return false
})

const handleChangedShifts = () => {
    store.isCurrentShiftChanged = isShiftChanged.value
    if (store.currentShift?.id) {
        if (isShiftChanged.value) {
            store.addChangedShiftId(store.currentShift.id)
            // store.updateCurrentShifts(shift.value)
        }
        else {
            store.removeChangedShiftId(store.currentShift.id)
        }
    }
}

watch([position, dayType, start, end, startFact, endFact, isOvertime, durationDisplay, planStartTimeValid, planEndTimeValid, factStartTimeValid, factEndTimeValid], () => {
    if (!shift.value) return

    if (isOvertimeLockedByDayStateDocument.value && !isOvertime.value) {
        isOvertime.value = true
    }

    shift.value.position = position.value.id
    checkValidPosition()
    shift.value.day_type = dayType.value.id
    if (!isWorkday.value) {
        // Для нерабочих дней план null
        start.value = null
        end.value = null
        shift.value.plan_work_start_at = null
        shift.value.plan_work_end_at = null
    } else {
        if (dayjs(`${shift.value.date} ${start.value}`).isValid()) {
            shift.value.plan_work_start_at = dayjs.utc(`${shift.value.date} ${start.value}`).toISOString()
        }
        // add next day if time end less than start plan
        shift.value.plan_work_end_at = endTime(start.value, end.value, shift.value.date)

        if (dayjs(`${shift.value.date} ${startFact.value}`).isValid()) {
            shift.value.fact_work_start_at = dayjs.utc(`${factStartDate.value} ${startFact.value}`).toISOString()
        }

        if (startFact.value === null) shift.value.fact_work_start_at = startFact.value
        if (endFact.value === null) shift.value.fact_work_end_at = endFact.value

        if (dayjs(`${shift.value.date} ${endFact.value}`).isValid()) {
            // add next day if time end less than start fact
            shift.value.fact_work_end_at = endTime(startFact.value, endFact.value, factStartDate.value)
        }
    }

    shift.value.is_overtime = isOvertime.value

    if (shift.value.day_state_document) {
        shift.value.duration = pickerDurationToDecimalHours(durationDisplay.value)
    }

    // handle shift changes
    const isPlanTimeValid = validatePlanTime(false)
    handleChangedShifts()
    store.setCurrentShift(shift.value)
    if (!isPlanTimeValid || !areTimePickersValid.value) {
        store.isSaveShiftEnabled = false
        return
    }
    store.handleSaveButtonAvailability()
})

const validatePlanTime = (showError = true) => {
    const isValid = (!requiresPlanTime.value || (!!start.value && !!end.value)) &&
        planStartTimeValid.value &&
        planEndTimeValid.value &&
        factStartTimeValid.value &&
        factEndTimeValid.value

    if (isValid) planTimeError.value = false
    else if (showError) planTimeError.value = true

    if (showError && overtimeFactStartError.value) {
        toast.error(overtimeFactStartErrorMessage)
    }

    return isValid && !overtimeFactStartError.value
}

const endTime = (start: any, end: any, date: any) => {
    if (start < end) {
        return dayjs.utc(`${date} ${end}`).toISOString()
    } else if (start && end) {
        const nextDay = dayjs(date).add(1, 'day').format('YYYY-MM-DD')
        return dayjs.utc(`${nextDay} ${end}`).toISOString()
    }
}

const onDelete = async () => {
    emit('delete')
}

const shiftFieldClick = () => {
    store.shiftFieldsClicked = true
}

onMounted(() => {
    if (!shift.value) return
    if (!store.currentShift) {
        store.setCurrentShift(shift.value)
    }
    if (isOvertimeLockedByDayStateDocument.value) {
        isOvertime.value = true
        shift.value.is_overtime = true
    }

    shift.value.plan_work_end_at = endTime(start.value, end.value, shift.value.date)
    checkValidPosition()
})

defineExpose({
    validatePlanTime
})

</script>

<style>
/* noinspection CssUnusedSymbol */
.text-moderate {
    color: #FED36B
}

/* noinspection CssUnusedSymbol */
.text-ok {
    color: #a6bd63
}

/* noinspection CssUnusedSymbol */
.text-warning {
    color: #FA7E48
}
</style>
